<app-modal-wrapper
  [title]="editingId ? 'Edit API Client' : 'New API Client'"
  [loading]="initialLoading"
  (closeRequest)="tryClose()"
>
  <div modalSkeleton class="flex flex-col gap-6">
    <div><p-skeleton height="12px" width="70px" styleClass="mb-2"></p-skeleton><p-skeleton height="38px" borderRadius="6px"></p-skeleton></div>
    <div><p-skeleton height="12px" width="60px" styleClass="mb-2"></p-skeleton><p-skeleton height="38px" borderRadius="6px"></p-skeleton></div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div><p-skeleton height="12px" width="90px" styleClass="mb-2"></p-skeleton><p-skeleton height="38px" borderRadius="6px"></p-skeleton></div>
      <div><p-skeleton height="12px" width="60px" styleClass="mb-2"></p-skeleton><p-skeleton height="38px" borderRadius="6px"></p-skeleton></div>
    </div>
    <p-skeleton height="220px" borderRadius="8px"></p-skeleton>
  </div>

  <p class="-mt-2 mb-2 text-sm text-gray-500">
    Register an external service and grant only the endpoints it needs.
  </p>

  <ng-container *ngIf="!generatedSecret; else secretView">
    <form [formGroup]="form" class="flex flex-col gap-6">
      <app-input-dropdown
        label="Tenant"
        [options]="tenants"
        optionLabel="name"
        optionValue="id"
        placeholder="Select a tenant..."
        formControlName="tenantId"
        [required]="!editingId"
      ></app-input-dropdown>

      <app-input-text
        label="Name"
        formControlName="name"
        placeholder="External ERP"
        [required]="true"
      ></app-input-text>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-bold uppercase tracking-wide text-gray-700">Validity Days</label>
          <p-inputNumber
            formControlName="validityDays"
            [min]="1"
            [max]="3650"
            [useGrouping]="false"
            inputStyleClass="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[var(--primary-500)] focus:ring-1 focus:ring-[var(--primary-500)]"
            styleClass="w-full"
          ></p-inputNumber>
          <span class="text-xs text-gray-500">Up to 10 years (3650 days).</span>
        </div>

        <div class="flex items-center gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
          <div class="relative flex h-5 w-5 items-center justify-center">
            <input
              type="checkbox"
              formControlName="enabled"
              class="peer h-5 w-5 appearance-none rounded border border-gray-300 bg-white transition-all checked:border-[var(--primary-500)] checked:bg-[var(--primary-500)] focus:ring-0 cursor-pointer"
            />
            <i class="pi pi-check pointer-events-none absolute text-[10px] font-bold text-white opacity-0 transition-opacity duration-200 peer-checked:opacity-100"></i>
          </div>
          <label class="select-none text-sm font-bold text-gray-700 cursor-pointer">Enabled</label>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-bold uppercase tracking-wide text-gray-700">Allowed Endpoint Permissions</label>
        <p class="text-xs text-gray-500 -mt-0.5">
          Only endpoints selected here will be accepted by the generated API key.
        </p>
      </div>

      <div class="p-4 flex flex-col md:flex-row gap-4 bg-gray-50/30 overflow-hidden flex-1 min-h-0 rounded-lg border border-gray-200">
        <!-- Available Permissions -->
        <div class="flex flex-col flex-1 border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden max-h-[300px]">
          <div class="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 shrink-0">
            <div class="relative flex items-center justify-center w-5 h-5 cursor-pointer">
              <input
                type="checkbox"
                class="peer appearance-none w-5 h-5 border border-gray-300 rounded bg-white checked:bg-[var(--primary-500)] checked:border-[var(--primary-500)] cursor-pointer transition-all"
                [checked]="isAllSelected(availablePermissions)"
                (change)="toggleSelectAll(availablePermissions, $event)"
              />
              <i class="pi pi-check absolute text-white text-[10px] font-bold opacity-0 peer-checked:opacity-100 pointer-events-none"></i>
            </div>
            <span class="font-bold text-gray-700 text-sm select-none">Available</span>
          </div>

          <div class="p-3 border-b border-gray-100 shrink-0">
            <div class="relative">
              <input
                placeholder="Search.."
                [(ngModel)]="searchAvailable"
                [ngModelOptions]="{standalone: true}"
                class="w-full pl-3 pr-8 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:border-[var(--primary-500)] focus:ring-1 focus:ring-[var(--primary-500)] transition-all"
              />
              <i class="pi pi-search absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto custom-scrollbar p-2 bg-white">
            <ng-container *ngFor="let permission of filteredAvailable()">
              <div class="flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-50 cursor-pointer" (click)="permission.selected = !permission.selected">
                <div class="relative flex items-center justify-center w-5 h-5">
                  <input type="checkbox" [(ngModel)]="permission.selected" [ngModelOptions]="{standalone: true}" class="appearance-none w-5 h-5 border rounded bg-white cursor-pointer transition-all" [ngStyle]="{'background-color': permission.selected ? 'var(--primary-500)' : 'white', 'border-color': permission.selected ? 'var(--primary-500)' : ''}" />
                  <i class="pi pi-check absolute text-white text-[10px] font-bold pointer-events-none" [style.opacity]="permission.selected ? '1' : '0'"></i>
                </div>
                <div class="text-sm text-gray-700 truncate">{{ permission.label }}</div>
              </div>
            </ng-container>

            <div *ngIf="filteredAvailable().length === 0" class="flex flex-col items-center justify-center h-full text-gray-400 pb-8 py-8">
              <span class="text-xs italic">No permissions found</span>
            </div>
          </div>
        </div>

        <!-- Transfer Buttons -->
        <div class="flex flex-row md:flex-col justify-center items-center gap-3 shrink-0">
          <div class="flex flex-row md:flex-col gap-3">
            <button pButton type="button" icon="pi pi-arrow-right" class="!bg-[var(--primary-500)] !border-[var(--primary-500)] !text-white w-10 h-10 rounded-lg shadow-md" [disabled]="!hasSelected(availablePermissions)" (click)="moveToSelected()"></button>
            <button pButton type="button" icon="pi pi-arrow-left" class="!bg-[var(--primary-500)] !border-[var(--primary-500)] !text-white w-10 h-10 rounded-lg shadow-md" [disabled]="!hasSelected(selectedPermissions)" (click)="moveToAvailable()"></button>
          </div>
        </div>

        <!-- Selected Permissions -->
        <div class="flex flex-col flex-1 border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden max-h-[300px]">
          <div class="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 shrink-0">
            <div class="relative flex items-center justify-center w-5 h-5 cursor-pointer">
              <input type="checkbox" class="peer appearance-none w-5 h-5 border border-gray-300 rounded bg-white" [checked]="isAllSelected(selectedPermissions)" (change)="toggleSelectAll(selectedPermissions, $event)" />
            </div>
            <span class="font-bold text-gray-700 text-sm select-none">Selected</span>
          </div>

          <div class="p-3 border-b border-gray-100 shrink-0">
            <div class="relative">
              <input placeholder="Search.." [(ngModel)]="searchSelected" [ngModelOptions]="{standalone: true}" class="w-full pl-3 pr-8 py-2 text-xs border border-gray-300 rounded-md" />
              <i class="pi pi-search absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto custom-scrollbar p-2 bg-white">
            <ng-container *ngFor="let permission of filteredSelected()">
              <div class="flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-50 cursor-pointer" (click)="permission.selected = !permission.selected">
                <div class="relative flex items-center justify-center w-5 h-5">
                  <input type="checkbox" [(ngModel)]="permission.selected" [ngModelOptions]="{standalone: true}" class="appearance-none w-5 h-5 border rounded bg-white cursor-pointer transition-all" [ngStyle]="{'background-color': permission.selected ? 'var(--primary-500)' : 'white', 'border-color': permission.selected ? 'var(--primary-500)' : ''}" />
                  <i class="pi pi-check absolute text-white text-[10px] font-bold pointer-events-none" [style.opacity]="permission.selected ? '1' : '0'"></i>
                </div>
                <div class="text-sm text-gray-700 truncate">{{ permission.label }}</div>
              </div>
            </ng-container>

            <div *ngIf="filteredSelected().length === 0" class="flex flex-col items-center justify-center h-full text-gray-400 pb-8 py-8">
              <span class="text-xs italic">No permissions selected</span>
            </div>
          </div>
        </div>
      </div>
    </form>
  </ng-container>

  <ng-template #secretView>
    <div class="rounded-2xl border border-emerald-100 bg-emerald-50 p-6 shadow-sm">
      <div class="flex items-start gap-3">
        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
          <i class="pi pi-check"></i>
        </div>
        <div class="min-w-0 flex-1">
          <h4 class="m-0 text-lg font-bold text-emerald-900">API client created</h4>
          <p class="mt-1 text-sm text-emerald-800">Copy the secret below now. It will not be shown again.</p>

          <div class="mt-4 rounded-xl border border-emerald-200 bg-white p-4">
            <div class="flex flex-col items-stretch gap-3">
              <div>
                <div class="text-xs font-bold uppercase tracking-wide text-gray-500">Generated Secret</div>
                <div class="mt-2 break-all rounded-lg bg-gray-950 px-4 py-3 font-mono text-sm text-emerald-200">
                  {{ generatedSecret }}
                </div>
              </div>

              <button
                pButton
                type="button"
                label="Copy"
                icon="pi pi-copy"
                iconPos="right"
                class="w-full border-none bg-[var(--primary-500)] px-4 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-[var(--primary-600)]"
                (click)="copySecret()"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-template>

  <div modalFooter class="flex items-center gap-3 flex-1 justify-between">
    <button
      *ngIf="editingId && !generatedSecret"
      pButton
      type="button"
      label="Regenerate Key"
      icon="pi pi-refresh"
      iconPos="right"
      class="border border-orange-400 bg-white px-6 py-3 text-sm font-bold text-orange-600 shadow-sm transition-all hover:bg-orange-50"
      [disabled]="regenerating"
      [loading]="regenerating"
      (click)="regenerateKey()"
    ></button>
    <div *ngIf="!editingId || generatedSecret" class="flex-1"></div>
    <button
      pButton
      type="button"
      [label]="generatedSecret ? 'Close' : 'Save'"
      [icon]="generatedSecret ? 'pi pi-times' : 'pi pi-save'"
      iconPos="right"
      class="border-none bg-[var(--primary-500)] px-8 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-[var(--primary-600)]"
      [disabled]="(!generatedSecret && form.invalid) || saving"
      [loading]="saving"
      (click)="save()"
    ></button>
  </div>
</app-modal-wrapper>
